/**
 * Browser-side desktop wire codec (T10).
 *
 * A deliberate duplicate of `packages/tunnel-protocol/src/desktop-stream.ts`,
 * which stays the source of truth. That module decodes media frames with
 * `Buffer`, and Vite does not polyfill Buffer, so importing it here would drag a
 * Node shim into the bundle or blow up on the first frame. The price of the copy
 * is drift, and drift on this path shows up as a black canvas with no error at
 * all — so `wire.spec.ts` pins the layout to hand-written bytes instead of
 * trusting the two implementations to stay in step by inspection.
 *
 * Everything here is browser-native: DataView over Uint8Array, BigInt for the
 * 64-bit pts.
 */

// ─── Endpoints and version ───

export const WS_VIEWER_PATH = '/ws/viewer';
export const DESKTOP_PROTOCOL_VERSION = 1;

// ─── Close codes (broker → viewer) ───

export const CLOSE_DESKTOP_SESSION_GONE = 4411;
export const CLOSE_DESKTOP_BUSY = 4412;
export const CLOSE_DESKTOP_VERSION = 4413;

// ─── Media frame binary layout ───
//
//   0  1  version    (DESKTOP_MEDIA_VERSION)
//   1  1  kind       (1 = video)
//   2  1  flags      (bit0 = keyframe)
//   3  1  rectCount  (0 = full frame)
//   4  4  seq        u32 BE
//   8  8  ptsMicros  u64 BE
//  16  8×N rects     (x, y, w, h — each u16 BE)

export const DESKTOP_MEDIA_VERSION = 1;
export const DESKTOP_MEDIA_HEADER_BYTES = 16;
export const DESKTOP_RECT_BYTES = 8;
export const DESKTOP_MAX_RECTS = 255;
export const DESKTOP_MAX_MEDIA_FRAME_BYTES = 4 * 1024 * 1024;
export const DESKTOP_MEDIA_VIDEO = 1;
export const DESKTOP_FLAG_KEYFRAME = 0x01;

// ─── Input ───

export const DESKTOP_MOD_SHIFT = 1;
export const DESKTOP_MOD_CTRL = 2;
export const DESKTOP_MOD_ALT = 4;
export const DESKTOP_MOD_META = 8;
/** One `desktop_input` carries at most this many coalesced events. */
export const DESKTOP_MAX_INPUT_EVENTS = 64;
/** The control codec rejects wheel deltas outside ±16, so clamp before sending. */
export const DESKTOP_MAX_WHEEL = 16;
/** Clipboard and bitrate bounds the control codec enforces on the far side. */
export const DESKTOP_MIN_BITRATE_BPS = 300_000;
export const DESKTOP_MAX_BITRATE_BPS = 8_000_000;

/** Thrown on malformed framing; the caller drops the stream rather than guessing. */
export class DesktopProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DesktopProtocolError';
  }
}

export interface DamageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DesktopMediaFrame {
  kind: number;
  keyframe: boolean;
  seq: number;
  /** Microseconds since session start; 64-bit on the wire so it does not wrap. */
  ptsMicros: number;
  /** Empty means "full frame". */
  rects: DamageRect[];
  /** A view into the received buffer — not a copy. */
  payload: Uint8Array;
}

export function decodeDesktopMediaFrame(data: ArrayBuffer | Uint8Array): DesktopMediaFrame {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.byteLength < DESKTOP_MEDIA_HEADER_BYTES) {
    throw new DesktopProtocolError(`media frame too short: ${bytes.byteLength} bytes`);
  }
  if (bytes.byteLength > DESKTOP_MAX_MEDIA_FRAME_BYTES) {
    throw new DesktopProtocolError(`media frame too large: ${bytes.byteLength} bytes`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = view.getUint8(0);
  if (version !== DESKTOP_MEDIA_VERSION) {
    throw new DesktopProtocolError(`unsupported media frame version: ${version}`);
  }
  const rectCount = view.getUint8(3);
  const rectsEnd = DESKTOP_MEDIA_HEADER_BYTES + rectCount * DESKTOP_RECT_BYTES;
  if (bytes.byteLength < rectsEnd) {
    throw new DesktopProtocolError(`media frame truncated: ${rectCount} rects do not fit`);
  }
  const rects: DamageRect[] = [];
  for (let i = 0; i < rectCount; i++) {
    const at = DESKTOP_MEDIA_HEADER_BYTES + i * DESKTOP_RECT_BYTES;
    rects.push({
      x: view.getUint16(at),
      y: view.getUint16(at + 2),
      width: view.getUint16(at + 4),
      height: view.getUint16(at + 6),
    });
  }
  return {
    kind: view.getUint8(1),
    keyframe: (view.getUint8(2) & DESKTOP_FLAG_KEYFRAME) !== 0,
    seq: view.getUint32(4),
    ptsMicros: Number(view.getBigUint64(8)),
    rects,
    payload: bytes.subarray(rectsEnd),
  };
}

// ─── Control frames (JSON text) ───

export interface DesktopViewerHelloFrame {
  type: 'hello';
  jwt: string;
  sessionId: string;
  desktopProtocolVersion: number;
}

export interface DesktopHelloOkFrame {
  type: 'hello_ok';
  desktopProtocolVersion: number;
  role: 'child' | 'viewer';
}

export interface DesktopConfigFrame {
  type: 'desktop_config';
  width: number;
  height: number;
  /** WebCodecs codec string, e.g. "avc1.640028". */
  codec: string;
  fps: number;
  encoder: string;
  monitors?: number;
}

export interface DesktopAckFrame {
  type: 'desktop_ack';
  seq: number;
  bytes: number;
}

export interface DesktopKeyframeRequestFrame {
  type: 'desktop_keyframe_request';
}

export interface DesktopBitrateHintFrame {
  type: 'desktop_bitrate_hint';
  bps: number;
}

export type DesktopPointerAction = 'move' | 'down' | 'up';
export type DesktopKeyAction = 'down' | 'up';

export type DesktopInputEvent =
  | {
      kind: 'pointer';
      action: DesktopPointerAction;
      x: number;
      y: number;
      button?: number;
      modifiers?: number;
    }
  | {
      kind: 'wheel';
      x: number;
      y: number;
      dx: number;
      dy: number;
      modifiers?: number;
    }
  | {
      kind: 'key';
      action: DesktopKeyAction;
      code: string;
      modifiers?: number;
    };

export interface DesktopInputFrame {
  type: 'desktop_input';
  events: DesktopInputEvent[];
}

export interface DesktopCursorFrame {
  type: 'desktop_cursor';
  x: number;
  y: number;
  visible: boolean;
  shapeId?: string;
}

export interface DesktopClipboardFrame {
  type: 'desktop_clipboard';
  text: string;
}

export interface DesktopErrorFrame {
  type: 'desktop_error';
  code: string;
  message: string;
}

/** Mirrors DESKTOP_CONSENT_STATES in desktop-stream.ts. */
export const DESKTOP_CONSENT_STATES = [
  'pending',
  'granted',
  'denied',
  'timeout',
  'unsupported',
] as const;
export type DesktopConsentState = (typeof DESKTOP_CONSENT_STATES)[number];

/**
 * Child → viewer: where the on-box Yes/No prompt stands. Only sent by a box
 * whose owner set `desktopConsent` (a customer's own PC); a planted box goes
 * straight to `desktop_config`.
 */
export interface DesktopConsentFrame {
  type: 'desktop_consent';
  state: DesktopConsentState;
  /** With `pending`: how long the box waits before declining on its own. */
  timeoutMs?: number;
}

export interface DesktopCloseFrame {
  type: 'desktop_close';
  reason?: string;
}

/** Everything the broker may hand a viewer. */
export type DesktopInboundFrame =
  | DesktopHelloOkFrame
  | DesktopConfigFrame
  | DesktopCursorFrame
  | DesktopClipboardFrame
  | DesktopConsentFrame
  | DesktopErrorFrame
  | DesktopCloseFrame;

/** Everything a viewer may send. */
export type DesktopOutboundFrame =
  | DesktopViewerHelloFrame
  | DesktopAckFrame
  | DesktopKeyframeRequestFrame
  | DesktopBitrateHintFrame
  | DesktopInputFrame
  | DesktopClipboardFrame
  | DesktopCloseFrame;

export function encodeDesktopFrame(frame: DesktopOutboundFrame): string {
  return JSON.stringify(frame);
}

function str(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function requireNormalized(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
    throw new DesktopProtocolError(`${field} must be a number in 0..1, got ${String(value)}`);
  }
  return value;
}

function requirePositiveInt(value: unknown, field: string, max: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > max) {
    throw new DesktopProtocolError(`${field} must be an integer in 1..${max}, got ${String(value)}`);
  }
  return value;
}

/**
 * Parse one control frame. Strict on purpose, matching the Node side: the
 * hello-time version check is what makes strictness safe, and a frame we do not
 * recognise means the peer is speaking a version we did not negotiate.
 */
export function parseDesktopControlFrame(data: string): DesktopInboundFrame {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    throw new DesktopProtocolError('desktop frame is not valid JSON');
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new DesktopProtocolError('desktop frame must be a JSON object');
  }
  const obj = raw as Record<string, unknown>;

  switch (obj.type) {
    case 'hello_ok': {
      if (obj.role !== 'child' && obj.role !== 'viewer') {
        throw new DesktopProtocolError(`invalid hello_ok role: ${String(obj.role)}`);
      }
      return {
        type: 'hello_ok',
        role: obj.role,
        desktopProtocolVersion: requirePositiveInt(
          obj.desktopProtocolVersion,
          'desktopProtocolVersion',
          0xffff,
        ),
      };
    }
    case 'desktop_config': {
      if (!str(obj.codec) || obj.codec.length > 64) {
        throw new DesktopProtocolError('desktop_config codec must be a short string');
      }
      if (!str(obj.encoder) || obj.encoder.length > 32) {
        throw new DesktopProtocolError('desktop_config encoder must be a short string');
      }
      const frame: DesktopConfigFrame = {
        type: 'desktop_config',
        width: requirePositiveInt(obj.width, 'width', 0xffff),
        height: requirePositiveInt(obj.height, 'height', 0xffff),
        codec: obj.codec,
        fps: requirePositiveInt(obj.fps, 'fps', 240),
        encoder: obj.encoder,
      };
      if (obj.monitors !== undefined) {
        frame.monitors = requirePositiveInt(obj.monitors, 'monitors', 64);
      }
      return frame;
    }
    case 'desktop_cursor': {
      if (typeof obj.visible !== 'boolean') {
        throw new DesktopProtocolError('desktop_cursor visible must be a boolean');
      }
      const frame: DesktopCursorFrame = {
        type: 'desktop_cursor',
        x: requireNormalized(obj.x, 'cursor.x'),
        y: requireNormalized(obj.y, 'cursor.y'),
        visible: obj.visible,
      };
      if (obj.shapeId !== undefined) {
        if (!str(obj.shapeId) || obj.shapeId.length > 64) {
          throw new DesktopProtocolError('desktop_cursor shapeId must be a short string');
        }
        frame.shapeId = obj.shapeId;
      }
      return frame;
    }
    case 'desktop_clipboard': {
      if (typeof obj.text !== 'string') {
        throw new DesktopProtocolError('desktop_clipboard text must be a string');
      }
      return { type: 'desktop_clipboard', text: obj.text };
    }
    case 'desktop_consent': {
      if (
        typeof obj.state !== 'string' ||
        !(DESKTOP_CONSENT_STATES as readonly string[]).includes(obj.state)
      ) {
        throw new DesktopProtocolError(`invalid desktop_consent state: ${String(obj.state)}`);
      }
      const frame: DesktopConsentFrame = {
        type: 'desktop_consent',
        state: obj.state as DesktopConsentState,
      };
      if (obj.timeoutMs !== undefined) {
        frame.timeoutMs = requirePositiveInt(obj.timeoutMs, 'timeoutMs', 3_600_000);
      }
      return frame;
    }
    case 'desktop_error': {
      if (!str(obj.code) || obj.code.length > 64) {
        throw new DesktopProtocolError('desktop_error code must be a short string');
      }
      if (typeof obj.message !== 'string' || obj.message.length > 512) {
        throw new DesktopProtocolError('desktop_error message must be a bounded string');
      }
      return { type: 'desktop_error', code: obj.code, message: obj.message };
    }
    case 'desktop_close': {
      const frame: DesktopCloseFrame = { type: 'desktop_close' };
      if (obj.reason !== undefined) {
        if (typeof obj.reason !== 'string' || obj.reason.length > 256) {
          throw new DesktopProtocolError('desktop_close reason must be a bounded string');
        }
        frame.reason = obj.reason;
      }
      return frame;
    }
    default:
      throw new DesktopProtocolError(`unknown desktop frame type: ${String(obj.type)}`);
  }
}

/**
 * One operator-facing sentence per way the on-box prompt can end without a
 * Yes. `pending`/`granted` are states, not outcomes, and have no sentence.
 */
export function describeDesktopConsent(state: DesktopConsentState): string {
  switch (state) {
    case 'denied':
      return 'The person at this PC declined the screen request.';
    case 'timeout':
      return 'Nobody at this PC answered the screen request in time, so it was declined.';
    case 'unsupported':
      return 'This PC is set to ask the person first, but its screen-capture component cannot ask. Reinstall the capture child there.';
    default:
      return 'This PC did not allow the screen request.';
  }
}

/** One operator-facing sentence per close code the broker uses. */
export function describeDesktopClose(code: number, reason: string): string {
  switch (code) {
    case CLOSE_DESKTOP_SESSION_GONE:
      return 'The screen session ended.';
    case CLOSE_DESKTOP_BUSY:
      return 'Someone else is already viewing this screen.';
    case CLOSE_DESKTOP_VERSION:
      return 'This box speaks a screen protocol this dashboard does not. Upgrade one of them.';
    default:
      return reason || 'The connection to the broker closed.';
  }
}

// ─── Input mapping (§8) ───

/** The element box an input event is measured against; `getBoundingClientRect()` shaped. */
export interface ViewerBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function modifierMask(event: {
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}): number {
  return (
    (event.shiftKey ? DESKTOP_MOD_SHIFT : 0) |
    (event.ctrlKey ? DESKTOP_MOD_CTRL : 0) |
    (event.altKey ? DESKTOP_MOD_ALT : 0) |
    (event.metaKey ? DESKTOP_MOD_META : 0)
  );
}

/** `KeyboardEvent.code` values the far-side codec accepts; anything else is dropped. */
export function isSendableKeyCode(code: string): boolean {
  return /^[A-Za-z0-9]{1,32}$/.test(code);
}

/** The scale and letterbox offsets of a `object-fit: contain` canvas. */
function contentBox(box: ViewerBox, remoteWidth: number, remoteHeight: number) {
  const scale = Math.min(box.width / remoteWidth, box.height / remoteHeight);
  const width = remoteWidth * scale;
  const height = remoteHeight * scale;
  return { width, height, offsetX: (box.width - width) / 2, offsetY: (box.height - height) / 2 };
}

/**
 * Viewport point → normalized 0..1 desktop coordinate.
 *
 * The canvas keeps the remote aspect ratio and letterboxes inside its element
 * box, so the element box and the drawn image are different rectangles whenever
 * the window shape differs from the desktop's. Mapping against the element box
 * puts every click tens of pixels off, silently, in a way that reads as the
 * remote machine misbehaving. Points in the letterbox bars are not over the
 * desktop at all and return null rather than a clamped lie.
 */
export function normalizedPointerPosition(
  clientX: number,
  clientY: number,
  box: ViewerBox,
  remoteWidth: number,
  remoteHeight: number,
): { x: number; y: number } | null {
  if (remoteWidth <= 0 || remoteHeight <= 0 || box.width <= 0 || box.height <= 0) return null;
  const content = contentBox(box, remoteWidth, remoteHeight);
  const x = (clientX - box.left - content.offsetX) / content.width;
  const y = (clientY - box.top - content.offsetY) / content.height;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
}

/** The inverse: a normalized desktop point → pixels inside the element box. */
export function desktopPointToBox(
  x: number,
  y: number,
  box: ViewerBox,
  remoteWidth: number,
  remoteHeight: number,
): { left: number; top: number } {
  const content = contentBox(box, remoteWidth, remoteHeight);
  return { left: content.offsetX + x * content.width, top: content.offsetY + y * content.height };
}

/**
 * DOM wheel delta → the notch-ish units the child maps to WHEEL_DELTA. Line and
 * page modes are resolved here because only the browser knows what a "line" is
 * on this page, and the control codec rejects anything outside ±16.
 */
export function wheelNotches(delta: number, deltaMode: number, pagePixels: number): number {
  const LINE_PIXELS = 16;
  const NOTCH_PIXELS = 100;
  const pixels =
    deltaMode === 1 ? delta * LINE_PIXELS : deltaMode === 2 ? delta * pagePixels : delta;
  const notches = pixels / NOTCH_PIXELS;
  if (!Number.isFinite(notches)) return 0;
  return Math.max(-DESKTOP_MAX_WHEEL, Math.min(DESKTOP_MAX_WHEEL, notches));
}
